import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { UiPreferenceValues, useBackofficeUiPreferences } from 'src/shared/backofficeUiPreferences';
import type { ElementSize } from 'src/shared/hooks/useElementSize';

export type PropertiesPanelDock = 'right' | 'side';
export type WorkspaceLayoutMode = 'workspace' | 'drawer';
export type SectionRails = 'compact' | 'expanded';
export type ColorScheme = 'system' | 'light' | 'dark';
export type PanelKey = 'left' | 'right' | 'assets';
export type MeasurementUnit = 'm' | 'cm' | 'mm' | 'in';

export interface WorkspacePreferences {
    colorScheme: ColorScheme;
    layoutMode: WorkspaceLayoutMode;
    propertiesPanel: PropertiesPanelDock;
    sectionRails: SectionRails;
    measurementUnit: MeasurementUnit;
    collapsed: PanelKey[];
    sizes: Record<PanelKey, number>;
}

// Le quattro scelte di chi lavora. Il resto di `WorkspacePreferences` è geometria (`sizes`,
// `collapsed`) o non è una scelta di nessuno (`layoutMode`).
export type WorkspacePreferenceKey = 'colorScheme' | 'propertiesPanel' | 'sectionRails' | 'measurementUnit';
export type WorkspacePreferencesSnapshot = Partial<Record<WorkspacePreferenceKey, string>>;

// Le preferenze stanno sul server, nel documento di `src/shared/backofficeUiPreferences.tsx`, sotto
// questo scope: le quattro scelte e le larghezze delle colonne. Contratto:
// docs/preferenze-interfaccia-microservizio.md.
const SCOPE = 'scene-editor';

// In locale resta solo `collapsed`: quali pannelli sono chiusi è uno stato del momento, come la
// sidebar. È la chiave che prima teneva tutto, ed è quindi anche quella da cui si migra.
const LOCAL_KEY = 'zakeke.sceneEditor.workspace';

// I default di fabbrica sono quelli del prototipo: le proprietà accanto alla lista, e i
// rail che scrivono i nomi. Non sono una preferenza di gusto — sono la schermata che
// chiunque apra per la prima volta deve vedere, ed è quella su cui il disegno è stato
// giudicato. L'altro attracco resta a un clic, in Settings → Interface.
const DEFAULT_PREFERENCES: WorkspacePreferences = {
    colorScheme: 'system',
    layoutMode: 'workspace',
    propertiesPanel: 'side',
    sectionRails: 'expanded',
    measurementUnit: 'm',
    collapsed: [],
    sizes: { left: 332, right: 332, assets: 284 }
};

const ALLOWED_VALUES: Record<WorkspacePreferenceKey, readonly string[]> = {
    colorScheme: ['system', 'light', 'dark'],
    propertiesPanel: ['right', 'side'],
    sectionRails: ['compact', 'expanded'],
    measurementUnit: ['m', 'cm', 'mm', 'in']
};
const PREFERENCE_KEYS = Object.keys(ALLOWED_VALUES) as WorkspacePreferenceKey[];

const PANELS: PanelKey[] = ['left', 'right', 'assets'];

// LA LARGHEZZA È DEL POSTO, NON DELLA SEZIONE: si salva «quanto è larga la prima colonna», non
// «quanto è largo l'Outliner», e le altre superfici contano i loro posti allo stesso modo. La
// striscia in basso è un'altezza, e per questo ha un nome suo.
const SIZE_KEYS: Record<PanelKey, string> = { left: 'panel1', right: 'panel2', assets: 'strip' };

const MAX_PANEL_SIZE = 560;
const RAIL_WIDTHS: Record<SectionRails, number> = { compact: 43, expanded: 70 };

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const getPanelMinimum = (panel: PanelKey, sectionRails: SectionRails) => {
    if (panel === 'assets') return 43;
    return 284 + RAIL_WIDTHS[sectionRails];
};

// Il default della superficie, riportato dentro i limiti: coi rail estesi il minimo di una colonna
// supera il suo default, e il default vero diventa quel minimo.
const getDefaultSize = (panel: PanelKey, sectionRails: SectionRails) =>
    clamp(DEFAULT_PREFERENCES.sizes[panel], getPanelMinimum(panel, sectionRails), MAX_PANEL_SIZE);

const getExtent = (panel: PanelKey, workspace: ElementSize | null) => (panel === 'assets' ? workspace?.height : workspace?.width) ?? 0;

// IL NUMERO SALVATO È UNA FRAZIONE DEL WORKSPACE, non un conto di pixel: un px misurato su un 27"
// su un portatile non vuol dire niente, una frazione vuol dire ancora quello che voleva dire.
// Quattro decimali: su 2000 px l'errore di riconversione sta sotto il mezzo pixel.
const toFraction = (panel: PanelKey, size: number, workspace: ElementSize | null) => {
    const extent = getExtent(panel, workspace);
    return extent > 0 ? (size / extent).toFixed(4) : undefined;
};

// E SI RIVALIDA A OGNI LETTURA, non si applica: il minimo dipende dai rail, e un valore salvato coi
// rail estesi può essere fuori scala con quelli compatti.
const resolveSize = (panel: PanelKey, stored: string | undefined, workspace: ElementSize | null, sectionRails: SectionRails) => {
    const fraction = Number(stored);
    const extent = getExtent(panel, workspace);
    if (stored === undefined || extent === 0 || !(fraction > 0 && fraction < 1)) return getDefaultSize(panel, sectionRails);
    return clamp(fraction * extent, getPanelMinimum(panel, sectionRails), MAX_PANEL_SIZE);
};

// Il confronto è con TUTTI i valori ammessi, non solo con quello diverso dal default:
// scritto all'incontrario, cambiare il default riscriverebbe la scelta di chi aveva
// preso l'altra.
const pick = <K extends WorkspacePreferenceKey>(key: K, value: string | undefined): WorkspacePreferences[K] =>
    (value !== undefined && ALLOWED_VALUES[key].includes(value) ? value : DEFAULT_PREFERENCES[key]) as WorkspacePreferences[K];

const resolvePreferences = (stored: UiPreferenceValues, collapsed: PanelKey[], workspace: ElementSize | null): WorkspacePreferences => {
    const sectionRails = pick('sectionRails', stored.sectionRails);

    return {
        colorScheme: pick('colorScheme', stored.colorScheme),
        // L'Inspector a cassetto non è una scelta offerta: lo step 5 lo vuole colonna, e nel
        // prototipo il cassetto non esiste. Per questo non si salva nemmeno.
        layoutMode: 'workspace',
        propertiesPanel: pick('propertiesPanel', stored.propertiesPanel),
        sectionRails,
        measurementUnit: pick('measurementUnit', stored.measurementUnit),
        collapsed,
        sizes: {
            left: resolveSize('left', stored[SIZE_KEYS.left], workspace, sectionRails),
            right: resolveSize('right', stored[SIZE_KEYS.right], workspace, sectionRails),
            assets: resolveSize('assets', stored[SIZE_KEYS.assets], workspace, sectionRails)
        }
    };
};

// Ciò che sta nella chiave locale. Finché la migrazione non è avvenuta ci sono ancora le voci di
// quando la chiave teneva tutto — le larghezze in pixel.
type LocalPreferences = Partial<Omit<WorkspacePreferences, 'sizes'>> & { sizes?: Partial<Record<PanelKey, number>> };

const readLocal = (): LocalPreferences => {
    if (typeof window === 'undefined') return {};

    try {
        const stored: unknown = JSON.parse(window.localStorage.getItem(LOCAL_KEY) ?? '{}');
        return typeof stored === 'object' && stored !== null ? (stored as LocalPreferences) : {};
    } catch {
        return {};
    }
};

const writeLocal = (value: LocalPreferences) => {
    if (typeof window !== 'undefined') window.localStorage.setItem(LOCAL_KEY, JSON.stringify(value));
};

const readCollapsed = (local: LocalPreferences): PanelKey[] =>
    Array.isArray(local.collapsed) ? local.collapsed.filter((panel): panel is PanelKey => PANELS.includes(panel)) : [];

const readLegacyPreferences = (local: LocalPreferences) => {
    const entries: Partial<Record<WorkspacePreferenceKey, string>> = {};
    for (const key of PREFERENCE_KEYS) {
        const value = local[key];
        if (typeof value === 'string' && ALLOWED_VALUES[key].includes(value)) entries[key] = value;
    }
    return entries;
};

export const useWorkspacePreferences = (workspace: ElementSize | null) => {
    const { values: stored, isStored, save, fill } = useBackofficeUiPreferences(SCOPE);
    const [collapsed, setCollapsed] = useState(() => readCollapsed(readLocal()));
    const preferences = useMemo(() => resolvePreferences(stored, collapsed, workspace), [stored, collapsed, workspace]);

    // LA MIGRAZIONE: ciò che questo browser si ricordava prima che le preferenze avessero un posto sul
    // server. Riempie i buchi e non corregge niente — se il server una scelta ce l'ha già, fatta qui o
    // su un altro computer, vince lui. La copia locale si butta solo quando il server ha confermato di
    // avere questa superficie: prima, una scrittura fallita se la porterebbe via.
    //
    // Prima del disegno, perché chi aveva scelto il tema scuro non veda un lampo chiaro. Le larghezze
    // erano pixel e diventano frazioni, quindi aspettano la prima misura del workspace.
    //
    // Con loro si tengono i RAIL DI ALLORA: «era il default?» va chiesto ai rail con cui quei pixel
    // sono stati salvati. Coi rail di adesso, un 332 salvato coi rail compatti — il loro default —
    // sembrerebbe una larghezza scelta, se la misura arriva prima che i rail portati su siano a schermo.
    // `undefined`: la chiave locale non è ancora stata letta; `null`: non c'è niente da portare.
    const legacySizes = useRef<{ sizes: NonNullable<LocalPreferences['sizes']>; sectionRails: SectionRails } | null | undefined>(undefined);

    useLayoutEffect(() => {
        const local = readLocal();
        const entries = readLegacyPreferences(local);
        if (Object.keys(entries).length === 0 && !local.sizes) return;
        if (legacySizes.current === undefined) {
            legacySizes.current = local.sizes ? { sizes: local.sizes, sectionRails: pick('sectionRails', entries.sectionRails) } : null;
            fill(entries);
        }
        if (isStored) writeLocal({ collapsed: readCollapsed(local) });
    }, [fill, isStored]);

    useLayoutEffect(() => {
        const legacy = legacySizes.current;
        if (!legacy || !workspace) return;
        legacySizes.current = null;

        const entries: Record<string, string> = {};
        for (const panel of PANELS) {
            const size = Number(legacy.sizes[panel]);
            if (!size || size === getDefaultSize(panel, legacy.sectionRails)) continue;
            const fraction = toFraction(panel, clamp(size, getPanelMinimum(panel, legacy.sectionRails), MAX_PANEL_SIZE), workspace);
            if (fraction) entries[SIZE_KEYS[panel]] = fraction;
        }
        fill(entries);
    }, [fill, workspace]);

    const setPreference = useCallback(<K extends WorkspacePreferenceKey>(key: K, value: WorkspacePreferences[K]) => save({ [key]: value }), [save]);

    // Si scrive al rilascio della presa, non a ogni movimento. E chi non è stato trascinato non si
    // scrive: tornare al default — il doppio clic sulla presa — toglie la voce, e il posto torna a
    // valere il default della superficie.
    const setPanelSize = useCallback(
        (panel: PanelKey, size: number) => {
            const fraction = size === getDefaultSize(panel, preferences.sectionRails) ? null : toFraction(panel, size, workspace);
            if (fraction !== undefined) save({ [SIZE_KEYS[panel]]: fraction });
        },
        [preferences.sectionRails, save, workspace]
    );

    const toggleCollapsed = useCallback((panel: PanelKey) => {
        setCollapsed((current) => {
            const next = current.includes(panel) ? current.filter((currentPanel) => currentPanel !== panel) : [...current, panel];
            // Le voci di prima della migrazione restano dove sono: chiudere un pannello non deve
            // portarsi via le scelte che il server non ha ancora confermato.
            writeLocal({ ...readLocal(), collapsed: next });
            return next;
        });
    }, []);

    // «Cancel» nelle Settings rimette le scelte com'erano, anche la loro assenza: una voce che non
    // c'era torna a non esserci, invece di diventare il default scritto per esteso.
    const snapshotPreferences = useCallback(
        (): WorkspacePreferencesSnapshot => Object.fromEntries(PREFERENCE_KEYS.filter((key) => stored[key] !== undefined).map((key) => [key, stored[key]])),
        [stored]
    );

    const restorePreferences = useCallback(
        (snapshot: WorkspacePreferencesSnapshot) => save(Object.fromEntries(PREFERENCE_KEYS.map((key) => [key, snapshot[key] ?? null]))),
        [save]
    );

    return useMemo(
        () => ({ preferences, setPreference, setPanelSize, toggleCollapsed, snapshotPreferences, restorePreferences }),
        [preferences, restorePreferences, setPanelSize, setPreference, snapshotPreferences, toggleCollapsed]
    );
};

export const getWorkspacePanelMinimum = (panel: PanelKey, sectionRails: SectionRails) => getPanelMinimum(panel, sectionRails);
export const getWorkspaceRailWidth = (sectionRails: SectionRails) => RAIL_WIDTHS[sectionRails];
export const WORKSPACE_MAX_PANEL_SIZE = MAX_PANEL_SIZE;
